/**
 * Uma tabela em que tudo é editável: a célula, o nome da coluna, e quantas colunas há.
 *
 * <p><b>Por que uma `<table>` de verdade</b>, e não uma lista de campos de colunas
 * fixas (o desenho da antiga `ListaEditavel`, aposentada com o rascunho de Combate).
 * Lá cada linha era um item solto e as colunas eram poucas e fixas. Aqui a tela existe
 * para <i>comparar</i> — o que o épico-01 diz contra o que o GDD diz, linha a linha —,
 * e comparar é ler na vertical. Uma lista de campos obriga o olho a saltar; a tabela
 * põe as duas colunas lado a lado, que é o pedido.
 *
 * <p><b>O cabeçalho é um campo, não um rótulo.</b> Parece detalhe de estilo e não é: a
 * coluna que vai faltar nesta página é a que ninguém previu, e uma tabela cujo
 * cabeçalho só se muda no código transforma "acrescentar uma coluna" num commit.
 *
 * <p>Reordenar é por <b>seta</b>: arrastar some inteiro para quem usa teclado ou
 * leitor de tela.
 */
import type { ReactNode } from 'react';

import * as t from './tabelasEditaveis';
import type { Linha, Tabela } from './tabelasEditaveis';
import './tabelaEditavel.css';

/**
 * A classe que pinta a linha, ou nada.
 *
 * <p>Prefixada: `fam-` deixa claro, ao ler o CSS, que aquele bloco de cores responde às
 * famílias de uma tabela e não a um estado da tela. E o nome da família vem do conteúdo,
 * então o prefixo também impede que um valor inesperado ali colida com uma classe de
 * verdade da folha de estilo.
 */
function classeDaFamilia(tabela: Tabela, idDaLinha: string): string | undefined {
  const familia = tabela.familias?.[idDaLinha];
  return familia ? `fam-${familia}` : undefined;
}

export function TabelaEditavel({
  tabela,
  aoMudar,
  rotuloDoItem = 'linha',
  numerada,
  cores,
  entregas,
  icone,
}: {
  tabela: Tabela;
  aoMudar: (nova: Tabela) => void;
  /** Como o botão chama uma linha nova: "linha", "conflito", "tipo". */
  rotuloDoItem?: string;
  /** Numera as linhas — usar quando a ordem <i>é</i> a informação. */
  numerada?: boolean;
  /**
   * O ícone de cada linha, resolvido pelo conteúdo dela — a perícia pelo rótulo, o
   * atributo pela sigla, a ação pela primeira palavra. Vem de fora pela mesma razão das
   * cores: o que é uma "Espada" é assunto da página, não desta tabela. Devolver
   * {@code null} deixa a célula vazia — linha que o time inventou e o sistema não
   * reconhece fica sem desenho, que é o certo.
   */
  icone?: (linha: Linha) => ReactNode;
  /**
   * As famílias que esta tabela agrupa por cor. Vazio ou ausente: sem coluna de cor.
   *
   * <p>Vêm de fora porque as famílias são do <b>conteúdo</b> — "Lugar", "NPC", "Evento"
   * são vocabulário do Mistik Island, e este componente não tem por que conhecê-las. Ele
   * sabe pintar uma linha e oferecer a troca; quais grupos existem é assunto da página.
   */
  cores?: { id: string; rotulo: string }[];
  /**
   * As entregas que cada linha pode marcar — Alpha interno, Closed Beta, Season 1,
   * Season 2+. Ausente: sem colunas de checkbox.
   *
   * <p>De fora pela mesma razão de {@code cores}: quais entregas existem é decisão do
   * planejamento do jogo, não deste componente. As colunas são fixas de propósito —
   * não se renomeiam nem se movem, porque são a mesma régua em toda tabela da página;
   * uma régua editável por quadro divergiria, e escopo comparado entre tabelas é o
   * ponto de marcá-lo.
   */
  entregas?: { id: string; rotulo: string }[];
}) {
  const podeApagarColuna = tabela.colunas.length > 1;
  const temCores = Boolean(cores?.length);
  const temEntregas = Boolean(entregas?.length);

  return (
    <div className="te">
      <div className="te-rolagem">
        <table className="te-tabela">
          <thead>
            <tr>
              {numerada && <th className="te-n" scope="col" aria-label="Número" />}
              {icone && <th className="te-icone" scope="col" aria-label="Ícone" />}

              {tabela.colunas.map((c, i) => (
                <th key={c.id} scope="col" className={c.larga ? 'larga' : undefined}>
                  <input
                    className="te-cabecalho"
                    value={c.rotulo}
                    placeholder="nome da coluna"
                    aria-label={`Nome da coluna ${i + 1}`}
                    onChange={(e) => aoMudar(t.renomearColuna(tabela, c.id, e.target.value))}
                  />
                  <div className="te-bts">
                    <button
                      type="button"
                      className="te-bt"
                      onClick={() => aoMudar(t.moverColuna(tabela, c.id, -1))}
                      disabled={i === 0}
                      title="Mover coluna para a esquerda"
                      aria-label={`Mover a coluna ${c.rotulo || i + 1} para a esquerda`}
                    >
                      ←
                    </button>
                    <button
                      type="button"
                      className="te-bt"
                      onClick={() => aoMudar(t.moverColuna(tabela, c.id, 1))}
                      disabled={i === tabela.colunas.length - 1}
                      title="Mover coluna para a direita"
                      aria-label={`Mover a coluna ${c.rotulo || i + 1} para a direita`}
                    >
                      →
                    </button>
                    <button
                      type="button"
                      className="te-bt apagar"
                      onClick={() => aoMudar(t.removerColuna(tabela, c.id))}
                      disabled={!podeApagarColuna}
                      title={
                        podeApagarColuna
                          ? 'Apagar esta coluna e o que está escrito nela'
                          : 'A última coluna não sai — a tabela ficaria sem onde escrever'
                      }
                      aria-label={`Apagar a coluna ${c.rotulo || i + 1}`}
                    >
                      ×
                    </button>
                  </div>
                </th>
              ))}

              {temEntregas &&
                entregas!.map((e) => (
                  <th key={e.id} className="te-entrega" scope="col" title={e.rotulo}>
                    {e.rotulo}
                  </th>
                ))}

              {temCores && (
                <th className="te-cor" scope="col">
                  Cor
                </th>
              )}

              <th className="te-acoes" scope="col">
                <button
                  type="button"
                  className="te-mais-coluna"
                  onClick={() => aoMudar(t.acrescentarColuna(tabela))}
                  title="Acrescentar uma coluna"
                  aria-label="Acrescentar uma coluna"
                >
                  + coluna
                </button>
              </th>
            </tr>
          </thead>

          <tbody>
            {tabela.linhas.map((linha, i) => (
              /*
               * A família pinta o fundo e não diz nada sozinha: o que a linha é continua
               * escrito nela. Linha sem família — as que o time acrescenta — fica sem
               * cor, que é o certo: o sistema não tem como adivinhar o parentesco dela.
               */
              <tr key={linha.id} className={classeDaFamilia(tabela, linha.id)}>
                {numerada && <td className="te-n">{i + 1}</td>}
                {icone && <td className="te-icone">{icone(linha)}</td>}

                {tabela.colunas.map((c) => (
                  <td key={c.id} className={c.larga ? 'larga' : undefined}>
                    <textarea
                      className="te-celula"
                      rows={1}
                      value={linha.valores[c.id] ?? ''}
                      aria-label={`${c.rotulo || 'coluna'} — ${rotuloDoItem} ${i + 1}`}
                      onChange={(e) =>
                        aoMudar(t.escreverCelula(tabela, linha.id, c.id, e.target.value))
                      }
                    />
                  </td>
                ))}

                {temEntregas &&
                  entregas!.map((e) => (
                    <td key={e.id} className="te-entrega">
                      <input
                        type="checkbox"
                        checked={tabela.entregas?.[linha.id]?.includes(e.id) ?? false}
                        aria-label={`${e.rotulo} — ${rotuloDoItem} ${i + 1}`}
                        title={e.rotulo}
                        onChange={() => aoMudar(t.alternarEntrega(tabela, linha.id, e.id))}
                      />
                    </td>
                  ))}

                {temCores && (
                  <td className="te-cor">
                    {/*
                      Um `select` nativo, e não uma paleta de amostras: cinco quadrados
                      coloridos exigiriam que a pessoa soubesse o que cada cor significa
                      antes de escolher — e é justamente isso que a coluna existe para
                      ensinar. O nome da família por extenso resolve, e vem de graça com
                      teclado e leitor de tela.
                    */}
                    {/*
                      Sem classe de família própria: o seletor herda as variáveis de cor
                      da `<tr>` que o contém, então ele já se veste sozinho. Repetir a
                      classe aqui daria dois lugares para a mesma cor sair — e um dia
                      sairiam diferentes.
                    */}
                    <select
                      className="te-seletor-cor"
                      value={tabela.familias?.[linha.id] ?? ''}
                      aria-label={`Cor da ${rotuloDoItem} ${i + 1}`}
                      onChange={(e) =>
                        aoMudar(t.escreverFamilia(tabela, linha.id, e.target.value))
                      }
                    >
                      {/* Só aparece em linha nova, que ainda não foi classificada. */}
                      <option value="" disabled>
                        —
                      </option>
                      {cores!.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.rotulo}
                        </option>
                      ))}
                    </select>
                  </td>
                )}

                <td className="te-acoes">
                  <div className="te-bts">
                    <button
                      type="button"
                      className="te-bt"
                      onClick={() => aoMudar(t.moverLinha(tabela, linha.id, -1))}
                      disabled={i === 0}
                      title="Subir"
                      aria-label={`Subir ${rotuloDoItem} ${i + 1}`}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="te-bt"
                      onClick={() => aoMudar(t.moverLinha(tabela, linha.id, 1))}
                      disabled={i === tabela.linhas.length - 1}
                      title="Descer"
                      aria-label={`Descer ${rotuloDoItem} ${i + 1}`}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="te-bt apagar"
                      onClick={() => aoMudar(t.removerLinha(tabela, linha.id))}
                      title="Apagar"
                      aria-label={`Apagar ${rotuloDoItem} ${i + 1}`}
                    >
                      ×
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {tabela.linhas.length === 0 && (
        <p className="te-vazio">Nenhuma {rotuloDoItem} ainda.</p>
      )}

      <button
        type="button"
        className="te-mais"
        onClick={() => aoMudar(t.acrescentarLinha(tabela))}
      >
        + {rotuloDoItem}
      </button>
    </div>
  );
}
